<template>
    <v-app>
        <header-card-public current_page="login"></header-card-public>
        <v-content class="bg-container">
            <v-layout fill-height row wrap>

                <!-- Left Side: Notifications (Desktop Only) -->
                <v-flex v-if="!isMobile" xs12 sm6 class="notifications-section">
                    <div class="notifications-bg">
                        <!-- <public-notifications></public-notifications> -->
                        <platform-showcase></platform-showcase>
                    </div>
                </v-flex>

                <!-- Right/bottom Side: Welcome Card -->
                <!-- Right Side: Interactive OTP Verification Section -->
                <v-flex xs12 sm6 :class="welcomeSectionClass">
                    <v-layout column fill-height align-center justify-center class="mobile-stack-container">
                        
                        <v-spacer v-if="isMobile"></v-spacer>

                        <div class="login-container">
                            <!-- Glassmorphic Form Card -->
                            <v-card :class="isMobile ? 'welcome-card-mobile' : 'welcome-card'" flat>
                                <v-form @submit.prevent="executeRecaptcha" ref="form">
                                    
                                    <!-- Typography Anchor locked into deep slate blue -->
                                    <h3 class="text-xs-center welcome-message card-dark-text">OTP Verification</h3>
                                    
                                    <v-card-text class="px-0">
                                        <v-alert v-if="error" :value="true" color="error" icon="error" class="rounded-4 mb-3" outline>
                                            {{errorMsg}}
                                        </v-alert>  
                                        <v-alert v-if="info" :value="true" color="info" icon="info" class="rounded-4 mb-3" outline>
                                            {{infoMsg}}
                                        </v-alert> 
                                        <v-alert v-if="this.$store.state.newDevice" :value="true" color="warning" icon="warning" class="rounded-4 mb-3" outline>
                                            Login from new device detected.
                                        </v-alert>  

                                        <p class="mt-20 body-1 card-secondary-text">Please enter the OTP we sent to your registered email address.</p>

                                        <!-- Form Input: Solo glass alignment rules -->
                                        <v-text-field
                                            ref="otpField"
                                            class="mt-20 brand-input-field"
                                            color="#1976D2"
                                            prepend-inner-icon="lock"
                                            v-model="otp"
                                            :rules="[rules.required, rules.min]"
                                            type="text"
                                            :disabled="disabled"
                                        ></v-text-field>
                                        
                                        <!-- Custom high-contrast countdown tracker string wrapper link -->
                                        <div v-if="hide_regenerate && expirationCounter > 0" class="otp-expiration-counter mt-10">
                                            <span><b>{{ expirationCounter }}</b> seconds remaining until expiration.</span>
                                        </div>
                                    </v-card-text>

                                    <!-- Micro opacity separation line breakdown -->
                                    <div class="glass-divider my-2"></div>
                                    
                                    <v-card-actions class="px-0 pt-3">
                                        <v-spacer></v-spacer>
                                        <!-- Primary Active Action Action Validation Button -->
                                        <v-btn color="#1976D2" type="submit" :loading="loading" @click="mode='validate'" class="no-uppercase rounded-4 primary-device-btn px-4 mr-2">
                                            Verify
                                        </v-btn>
                                        <!-- Secondary Regeneration Action Trigger Link Button -->
                                        <v-btn type="submit" :loading="loading_generate" v-if="!hide_regenerate" @click="mode='regenerate'" class="no-uppercase rounded-4 secondary-password-btn px-4">
                                            Regenerate
                                        </v-btn>
                                    </v-card-actions>
                                </v-form>  
                            </v-card>
                        </div>

                        <v-spacer v-if="isMobile"></v-spacer>

                        <!-- 💡 Fixed: Footer Area moved cleanly completely out of the card tag markup block boundary -->
                        <div class="global-desktop-footer caption">
                            <terms-privacy></terms-privacy>
                            <other-legal-docs></other-legal-docs>
                            <copyright></copyright>
                        </div>
                        
                    </v-layout>
                </v-flex>
                
            </v-layout>
        </v-content>

        <recaptcha ref="recaptcha" @verify="submitReCaptcha"></recaptcha>
    </v-app>
</template>

<script>

import '@/assets/css/public-pages.css'
import auth from "@/auth/auth.js";
import Recaptcha from "@/components/ReCaptcha.vue";
import securityApi from "@/api/security.js"
import publicPages from '@/helpers/public-pages.js'
import { getUAData } from '@/helpers/user-agent.js'

export default {
    name: 'Login',
    metaInfo: {
        title: 'Login',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Sign In With Google, Continue with Facebook, Sign In With Email' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        Recaptcha,
        'platform-showcase': () => import('@/components/PublicNotifications/PlatformShowcase'),
        'terms-privacy': () => import('@/components/Common/TermsAndPrivacy'),  
        'other-legal-docs': () => import('@/components/Common/OtherLegalDocs'),
        'copyright': () => import('@/components/Common/Copyright'),
        'header-card-public': () => import('@/components/Common/HeaderPublic'),
        'public-notifications': () => import('@/components/PublicNotifications/PublicNotifications'),
    },
    data () {
        return {
            error: false,
            errorMsg: null,
            info: false,
            infoMsg: null,
            loading: false,
            loading_generate: false,
            disabled: false,
            hide_regenerate: true,
            generateTimer: null,
            otp_timer: 0,
            otp_expiration: 300000, //5mins (300000ms), 1sec=1000ms
            otp: '',
            recaptchResponse: null,

            //todo - put this in rules-helper.js
            rules: {
                required: value => !!value || "OTP is required",
                min: v => (v && v.length === 6) || "OTP must be 6 digits"
            },
            userAgent: null,
        }
    },
    methods: {
        executeRecaptcha() {
            if (this.mode == 'validate') {
            if (this.$refs.form.validate()) {
                // execute the recaptcha widget
                this.$refs.recaptcha.execute()
            }
            } else {
            this.$refs.recaptcha.execute()
            }
        },
        submitReCaptcha(response) {
            this.recaptchResponse = response
            if (this.mode == 'validate') {
                this.validate()
            } else {
                this.regenerate()
            }
        },
        async validate() {
            this.loading = true
            this.disabled = true
            this.error = false

            try {
                const otpPayload = {
                    email: this.$store.state.email,
                    password: this.$store.state.pwd,
                    otp: this.otp,
                    recaptchaResponse: this.recaptchResponse
                }

                const response = await securityApi.verifyOTP(otpPayload)

                if (response.status === 200) {
                    await this.handleFinalLogin()
                }
            } catch (error) {
                this.error = true
                this.info = false
                this.errorMsg = this.parseOTPError(error)
            } finally {
                // Ensure UI is interactive again if an error occurred
                if (this.error) {
                    this.loading = false
                    this.disabled = false
                }
            }
        },
        async handleFinalLogin() {
            const loginData = {
                ...this.getDeviceInfo(), // Spread in a helper for browser/device info
                email: this.$store.state.email,
                password: this.$store.state.pwd,
                recaptchaResponse: this.recaptchResponse,
                sourceType: 'Customer'
            }

            await auth.login(loginData, true);

            if (!auth.loggedIn()) {
                throw new Error("Login failed after OTP verification.");
            }

            auth.clearLoginDetails()

            // Redirection Logic
            if (auth.onBoarded()) {
                const destination = this.$route.query.redirect || '/'
                this.$router.push(destination)
            } else {
                this.$router.push("/features")
            }
        },
        getDeviceInfo() {
            return {
                browserName: this.userAgent.browser.name,
                deviceModel: this.userAgent.device.model,
                deviceType: this.userAgent.device.type,
                deviceVendor: this.userAgent.device.vendor,
                oSName: this.userAgent.os.name,
                oSVersion: this.userAgent.os.version,
                deviceFingerprint: this.userAgent.fingerprint,
                deviceName: this.userAgent.deviceName
            };
        },
        parseOTPError(error) {
            const errorStr = error.toString();

            if (errorStr.includes("409")) {
                this.hide_regenerate = false;
                clearInterval(this.generateTimer);
                return "Invalid or expired OTP.";
            }

            if (errorStr.includes("401")) {
                this.hide_regenerate = true;
                return "You are not authorized to perform this action.";
            }

            // Traditional check for nested properties (No ?. operator)
            if (error.response && error.response.data && error.response.data.message) {
                return error.response.data.message;
            }

            return error.message || "An unexpected error occurred.";
        },
        async regenerate() {
            try {
                this.clearError()
                
                this.disabled = true
                this.loading_generate = true

                //generate otp then send to email
                var data = {
                    email: this.$store.state.email,
                    password: this.$store.state.pwd,
                    recaptchaResponse: this.recaptchResponse
                }
                const response = await securityApi.generateOTP(data)
                if (response.status == 200) {
                    this.info = true
                    this.infoMsg = "New OTP has been created and sent to your email."
                    this.$refs.otpField.focus()
                }

                //enable again regenerate button after 5mins
                clearInterval(this.generateTimer)
                this.otp_timer = 0
                this.disabled = false
                this.generateTimer = setInterval(this.enableRegenerate, 1000)

                this.disabled = false
                this.loading_generate = false
                this.hide_regenerate = true //disable regenerate button until 5mins expiration
            }catch(error){
                this.error = true
                this.info = false
                this.errorMsg = error
            }
        },
        enableRegenerate() {
            this.otp_timer += 1000

            if (this.otp_timer >= this.otp_expiration) {
                // STOP TIMER
                clearInterval(this.generateTimer)

                // FORCE FINAL STATE
                this.otp_timer = this.otp_expiration

                // SHOW EXPIRED ALERT
                this.error = true
                this.info = false
                this.errorMsg = "OTP has expired. Please regenerate a new one."

                // ENABLE REGENERATE BUTTON
                this.hide_regenerate = false

                // OPTIONAL: Disable OTP input
                this.disabled = true
            }
        },
        clearError() {
            this.error = false
            this.info = false
            this.errorMsg = null
            this.otp = ''
        },
        startOtpTimer() {
            if (this.generateTimer) clearInterval(this.generateTimer);
            this.generateTimer = setInterval(this.enableRegenerate, 1000);
        }
    },
    mounted() {
        // Focus the text field when component is mounted
        this.$nextTick(() => {
            if (this.$refs.otpField) {
                this.$refs.otpField.focus()
            }
        });
    },
    async created () {
        const redirectPath = this.$route.query.redirect || '/';

        // 1. Immediate redirect if already logged in (no flicker)
        if (auth.loggedIn()) {
            return this.$router.push(redirectPath);
        }

        // 2. Security Check: Block manual access if credentials are missing
        if (!this.$store.state.email || !this.$store.state.pwd) {
            return this.$router.push({
                path: '/signin-with-password',
                query: { redirect: this.$route.query.redirect }
            });
        }

        // 3. Initialize background tasks
        try {
            this.userAgent = await getUAData();
            this.startOtpTimer();
        } catch (err) {
            console.error("Failed to load device data:", err);
        }
    },
    destroyed() {
        clearInterval(this.generateTimer)
    },
    computed: {
        expirationCounter() {
            const remaining = (this.otp_expiration - this.otp_timer) / 1000
            return remaining > 0 ? remaining : 0
        },
        seasonDetails() {
            return publicPages.getSeasonDetails(this.$route.query.action, this.isMobile);
        },
        welcomeSectionClass() {
            return this.seasonDetails.className;
        },
    }
}
</script>